import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Star, Quote, Globe, Calendar, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';
import { API } from '../../lib/api';

const SOURCES = [
  { value: 'Booking.com', color: 'bg-blue-500' },
  { value: 'Google', color: 'bg-red-500' },
  { value: 'TripAdvisor', color: 'bg-green-500' },
  { value: 'Airbnb', color: 'bg-pink-500' },
  { value: 'Direct', color: 'bg-amber-500' },
];

const COUNTRIES = [
  'Italia', 'Germania', 'Francia', 'Regno Unito', 'Spagna', 'Olanda', 
  'Belgio', 'Austria', 'Svizzera', 'USA', 'Canada', 'Australia', 'Altro'
];

export default function AdminReviews() {
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  
  const [form, setForm] = useState({
    nome_ospite: '',
    nazionalita: 'Italia',
    testo: '',
    testo_en: '',
    rating: 10,
    data_soggiorno: '',
    fonte: 'Booking.com',
    avatar_url: '',
    ordine: 0,
    attivo: true
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/admin/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(res.data);
    } catch (error) {
      toast.error('Errore caricamento recensioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReview) {
        await axios.put(`${API}/admin/reviews/${editingReview.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recensione aggiornata!');
      } else {
        await axios.post(`${API}/admin/reviews`, { ...form, ordine: reviews.length }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recensione aggiunta!');
      }
      setDialogOpen(false);
      resetForm();
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Eliminare questa recensione?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recensione eliminata!');
      fetchReviews();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const toggleActive = async (review) => {
    try {
      await axios.put(`${API}/admin/reviews/${review.id}`, {
        ...review,
        attivo: !review.attivo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchReviews();
      toast.success(review.attivo ? 'Recensione nascosta' : 'Recensione visibile');
    } catch (error) {
      toast.error('Errore');
    }
  };

  const moveReview = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= reviews.length) return;

    const newReviews = [...reviews];
    [newReviews[index], newReviews[newIndex]] = [newReviews[newIndex], newReviews[index]];

    // Update order for both reviews
    try {
      await Promise.all([
        axios.put(`${API}/admin/reviews/${newReviews[index].id}`, {
          ...newReviews[index],
          ordine: index
        }, { headers: { Authorization: `Bearer ${token}` } }),
        axios.put(`${API}/admin/reviews/${newReviews[newIndex].id}`, {
          ...newReviews[newIndex],
          ordine: newIndex
        }, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      fetchReviews();
    } catch (error) {
      toast.error('Errore riordino');
    }
  };

  const openEdit = (review) => {
    setEditingReview(review);
    setForm({
      nome_ospite: review.nome_ospite,
      nazionalita: review.nazionalita || 'Italia',
      testo: review.testo,
      testo_en: review.testo_en || '',
      rating: review.rating,
      data_soggiorno: review.data_soggiorno || '',
      fonte: review.fonte,
      avatar_url: review.avatar_url || '',
      ordine: review.ordine,
      attivo: review.attivo
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingReview(null);
    setForm({
      nome_ospite: '',
      nazionalita: 'Italia',
      testo: '',
      testo_en: '',
      rating: 10,
      data_soggiorno: '',
      fonte: 'Booking.com',
      avatar_url: '',
      ordine: 0,
      attivo: true
    });
  };

  const getSourceColor = (fonte) => {
    const source = SOURCES.find(s => s.value === fonte);
    return source?.color || 'bg-gray-500';
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating / 2);
    const hasHalf = rating % 2 >= 1;
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`w-4 h-4 ${
              i < fullStars 
                ? 'fill-amber-400 text-amber-400' 
                : i === fullStars && hasHalf 
                  ? 'fill-amber-400/50 text-amber-400' 
                  : 'text-gray-300'
            }`} 
          />
        ))}
        <span className="ml-1 text-sm font-semibold text-amber-600">{rating}</span>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-cinzel text-[#1A202C]">Gestione Recensioni</h1>
            <p className="text-[#4A5568] font-manrope text-sm mt-1">
              Aggiungi recensioni da Booking.com, Google e altre fonti
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#C5A059] hover:bg-[#B08D45]" onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" /> Nuova Recensione
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cinzel">
                  {editingReview ? 'Modifica Recensione' : 'Nuova Recensione'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome Ospite *</Label>
                    <Input
                      value={form.nome_ospite}
                      onChange={(e) => setForm({ ...form, nome_ospite: e.target.value })}
                      required
                      placeholder="Es. Marco R."
                    />
                  </div>
                  <div>
                    <Label>Nazionalità</Label>
                    <select
                      value={form.nazionalita}
                      onChange={(e) => setForm({ ...form, nazionalita: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    >
                      {COUNTRIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fonte</Label>
                    <select
                      value={form.fonte}
                      onChange={(e) => setForm({ ...form, fonte: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    >
                      {SOURCES.map(s => (
                        <option key={s.value} value={s.value}>{s.value}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Rating (1-10)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      step="0.1"
                      value={form.rating}
                      onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Data Soggiorno</Label>
                  <Input
                    value={form.data_soggiorno}
                    onChange={(e) => setForm({ ...form, data_soggiorno: e.target.value })}
                    placeholder="Es. Agosto 2024"
                  />
                </div>

                <div>
                  <Label>Testo Recensione (IT) *</Label>
                  <Textarea
                    value={form.testo}
                    onChange={(e) => setForm({ ...form, testo: e.target.value })}
                    required
                    rows={4}
                    placeholder="Inserisci il testo della recensione in italiano..."
                  />
                </div>

                <div>
                  <Label>Testo Recensione (EN)</Label>
                  <Textarea
                    value={form.testo_en}
                    onChange={(e) => setForm({ ...form, testo_en: e.target.value })}
                    rows={4}
                    placeholder="Inserisci la traduzione in inglese (opzionale)..."
                  />
                </div>

                <div>
                  <Label>URL Avatar (opzionale)</Label>
                  <Input
                    value={form.avatar_url}
                    onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="attivo"
                    checked={form.attivo}
                    onChange={(e) => setForm({ ...form, attivo: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="attivo">Visibile sul sito</Label>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-[#C5A059] hover:bg-[#B08D45]">
                    {editingReview ? 'Salva' : 'Aggiungi'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-cinzel text-[#C5A059]">{reviews.length}</div>
              <div className="text-sm text-gray-500">Totale Recensioni</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-cinzel text-green-600">
                {reviews.filter(r => r.attivo).length}
              </div>
              <div className="text-sm text-gray-500">Visibili</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-cinzel text-amber-600">
                {reviews.length > 0 
                  ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
                  : '-'
                }
              </div>
              <div className="text-sm text-gray-500">Media Rating</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-cinzel text-blue-600">
                {reviews.filter(r => r.fonte === 'Booking.com').length}
              </div>
              <div className="text-sm text-gray-500">Da Booking.com</div>
            </CardContent>
          </Card>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059] mx-auto"></div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-16 bg-amber-50 rounded-xl border-2 border-dashed border-[#C5A059]">
            <Quote className="w-16 h-16 mx-auto text-[#C5A059] mb-4" />
            <h3 className="font-cinzel text-xl text-[#1A202C] mb-2">Nessuna Recensione</h3>
            <p className="text-gray-500 mb-4">Inizia aggiungendo le tue migliori recensioni da Booking.com</p>
            <Button 
              className="bg-[#C5A059] hover:bg-[#B08D45]"
              onClick={() => { resetForm(); setDialogOpen(true); }}
            >
              <Plus className="w-4 h-4 mr-2" /> Aggiungi Prima Recensione
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review, index) => (
              <Card key={review.id} className={`${!review.attivo ? 'opacity-60' : ''}`}>
                <CardContent className="py-4">
                  <div className="flex gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {review.avatar_url ? (
                        <img 
                          src={review.avatar_url} 
                          alt={review.nome_ospite}
                          className="w-14 h-14 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                          <span className="text-xl font-bold text-[#C5A059]">
                            {review.nome_ospite.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-[#1A202C]">{review.nome_ospite}</span>
                        {review.nazionalita && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {review.nazionalita}
                          </span>
                        )}
                        <span className={`text-xs text-white px-2 py-0.5 rounded ${getSourceColor(review.fonte)}`}>
                          {review.fonte}
                        </span>
                        {!review.attivo && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <EyeOff className="w-3 h-3" /> Nascosta
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3 mb-2">
                        {renderStars(review.rating)}
                        {review.data_soggiorno && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {review.data_soggiorno}
                          </span>
                        )}
                      </div>

                      <p className="text-gray-600 text-sm line-clamp-3">
                        "{review.testo}"
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => moveReview(index, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => moveReview(index, 'down')}
                        disabled={index === reviews.length - 1}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => toggleActive(review)}
                      >
                        {review.attivo ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(review)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600"
                        onClick={() => deleteReview(review.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
